import { CatalogImageOwner } from "../catalog/catalogQueries";
import { Product3DAssetReference, Product3DDimensions } from "./product3DTypes";

export type NormalizedProductDimensions = {
  dimensions: Product3DDimensions;
  unit: "m" | "cm" | "mm" | "unknown";
  confidence: "exact" | "inferred" | "unknown";
  source: string;
};

export function normalizeCatalogProductDimensions(
  product: Pick<
    CatalogImageOwner,
    "width" | "height" | "depth" | "supplier" | "categoriesMeta" | "categories"
  >,
): NormalizedProductDimensions {
  const raw = {
    width: sanitizeDimension(product.width),
    height: sanitizeDimension(product.height),
    depth: sanitizeDimension(product.depth),
  };
  const values = Object.values(raw).filter(
    (value): value is number => typeof value === "number",
  );
  if (values.length === 0) {
    return {
      dimensions: {},
      unit: "unknown",
      confidence: "unknown",
      source: "missing",
    };
  }

  const max = Math.max(...values);
  if (max > 1000) {
    return {
      dimensions: scaleDimensions(raw, 0.001),
      unit: "mm",
      confidence: "inferred",
      source: "magnitude:mm",
    };
  }

  if (max > 20) {
    return {
      dimensions: scaleDimensions(raw, 0.01),
      unit: "cm",
      confidence: "inferred",
      source: "magnitude:cm",
    };
  }

  return {
    dimensions: raw,
    unit: "m",
    confidence: "inferred",
    source: "magnitude:m",
  };
}

export function resolvePublishedProduct3DDimensions(
  product: Pick<
    CatalogImageOwner,
    "width" | "height" | "depth" | "supplier" | "categoriesMeta" | "categories"
  >,
  asset?: Pick<Product3DAssetReference, "dimensions">,
): NormalizedProductDimensions {
  const feed = normalizeCatalogProductDimensions(product);
  const dimensions = {
    width: sanitizeDimension(asset?.dimensions?.width) ?? feed.dimensions.width,
    depth: sanitizeDimension(asset?.dimensions?.depth) ?? feed.dimensions.depth,
    height:
      sanitizeDimension(asset?.dimensions?.height) ?? feed.dimensions.height,
  };
  const usesAsset = Object.values(asset?.dimensions ?? {}).some(
    (value) => sanitizeDimension(value) !== undefined,
  );
  return {
    dimensions,
    unit: usesAsset ? "m" : feed.unit,
    confidence: usesAsset ? "exact" : feed.confidence,
    source: usesAsset ? "publishedProduct3DAsset" : feed.source,
  };
}

export function scaleProduct3DDimensionsProportionally(
  dimensions: Product3DDimensions | undefined,
  anchor: "width" | "height" | "depth",
  value: number,
): Product3DDimensions | undefined {
  const currentAnchor = sanitizeDimension(dimensions?.[anchor]);
  if (!currentAnchor || !Number.isFinite(value) || value <= 0) return undefined;
  const scale = value / currentAnchor;
  const next = {
    width: scaleDimension(sanitizeDimension(dimensions?.width), scale),
    depth: scaleDimension(sanitizeDimension(dimensions?.depth), scale),
    height: scaleDimension(sanitizeDimension(dimensions?.height), scale),
  };
  return Object.values(next).every(
    (dimension) => sanitizeDimension(dimension) !== undefined,
  )
    ? next
    : undefined;
}

function sanitizeDimension(
  value: number | null | undefined,
): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

function scaleDimensions(
  dimensions: Product3DDimensions,
  scale: number,
): Product3DDimensions {
  return {
    width: scaleDimension(dimensions.width, scale),
    height: scaleDimension(dimensions.height, scale),
    depth: scaleDimension(dimensions.depth, scale),
  };
}

function scaleDimension(value: number | undefined, scale: number) {
  return typeof value === "number"
    ? Number((value * scale).toFixed(3))
    : undefined;
}
